// Hook: useFileUpload
//
// Orchestrates file uploads for the composer and the drop zone. The
// hook owns the per-file state (status, progress, cancel), talks to
// `matrix.ts` for the upload + typed-send round-trip, and hands the UI
// a narrow `UploadRecord[]` to render. All mimetype routing lives
// here — components stay presentational.
//
// Uploads run sequentially: the ticket explicitly calls out
// "Simultaneous multi-file progress" as out of scope, and serialising
// keeps backpressure simple (the homeserver is a shared resource; a
// handful of parallel uploads doesn't buy much in practice). Failures
// on one file don't cancel the queue — the user sees the failed row
// with a retry affordance and the rest proceeds.
//
// Image thumbnailing happens via an OffscreenCanvas-ish fallback to
// `<canvas>`: load the blob as an image, draw at the clamped size,
// then `toBlob` back out. The ticket pins the thumbnail at 800×800;
// we preserve aspect ratio and skip thumbnailing for images already
// smaller than the cap (no point re-encoding a 300×200 PNG).

import { useCallback, useEffect, useRef, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import {
  sendFile as matrixSendFile,
  sendImage as matrixSendImage,
  sendVideo as matrixSendVideo,
  type Client,
  type MediaThreadTarget,
} from "~/lib/matrix";
import { platform } from "~/lib/platform";
import { arriveWithTransition } from "~/lib/send-transition";

/** Cap in pixels for the client-generated image thumbnail. Ticket-pinned. */
const THUMBNAIL_MAX_DIMENSION = 800;

/** How long a terminal upload (success / cancel) stays visible in the
 *  composer before the hook prunes it. Long enough for the user to
 *  register the green tick on a fast upload, short enough that the
 *  progress bay doesn't accumulate forever. */
const AUTO_DISMISS_SUCCESS_MS = 1200;

export type UploadStatus = "uploading" | "sending" | "done" | "failed" | "cancelled";

export type UploadReason = "too_large" | "failed";

export interface UploadRecord {
  id: string;
  file: File;
  /** Integer percent in `[0, 100]`. Rust attachment sends do not expose
   *  byte-level progress yet, so the hook reports coarse phase changes. */
  progress: number;
  status: UploadStatus;
  error?: string;
  reason?: UploadReason;
  /** Thread the file was picked in; it never sends into another one. */
  threadRootId?: string;
}

export interface UseFileUploadResult {
  uploads: UploadRecord[];
  /**
   * Queue `files` for upload into `roomId`. The promise resolves once
   * every file has reached a terminal state (done / cancelled / failed).
   * Callers who just want fire-and-forget semantics can `void`-swallow
   * the returned promise.
   */
  uploadFiles: (files: readonly File[], roomId: string) => Promise<void>;
  /** Cancel an in-flight upload by record id. No-op for terminal
   *  records; for in-flight ones this aborts the current step and
   *  skips the queued tail. */
  cancel: (id: string) => void;
  /** Retry a failed upload. Re-queues the original `File`; the failed
   *  record is dropped from the list in favour of the fresh one. */
  retry: (id: string, roomId: string) => void;
}

/**
 * `thread` makes this a thread's uploader. Each file keeps the thread it
 * was picked in and follows that thread's latest reply when it sends.
 * `sendList` is the list a sent file should travel into (see
 * `arriveWithTransition`); without it the file appears in place.
 */
export function useFileUpload(
  thread?: () => MediaThreadTarget,
  sendList?: () => HTMLElement | null,
): UseFileUploadResult {
  const { client } = useMatrix();
  const threadRef = useRef(thread);
  threadRef.current = thread;
  const sendListRef = useRef(sendList);
  sendListRef.current = sendList;
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const autoDismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const mountedRef = useRef(true);
  // Rolling ref to the live list. Lets callbacks that fire outside
  // the React render cycle (timers, abort listeners) read the
  // current status without stale closures.
  const uploadsRef = useRef<UploadRecord[]>(uploads);
  uploadsRef.current = uploads;

  useEffect(() => {
    mountedRef.current = true;
    // Copy the ref contents into local bindings so the cleanup
    // function reads the set that existed at mount — React's lint
    // rule flags "ref.current in cleanup" because the ref can change
    // by the time cleanup runs. In our case these maps are stable
    // references, but local bindings make that invariant explicit.
    const timers = autoDismissTimersRef.current;
    const controllers = abortControllersRef.current;
    return () => {
      mountedRef.current = false;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      for (const c of controllers.values()) c.abort();
      controllers.clear();
    };
  }, []);

  // Safe setState — guards against resolving a promise after the
  // component unmounts (room switch, logout). The hook mounts at the
  // Timeline level, so an unmount equals "user left the room"; the
  // records go with it.
  const safeSetUploads = useCallback((updater: (prev: UploadRecord[]) => UploadRecord[]) => {
    if (!mountedRef.current) return;
    setUploads(updater);
  }, []);

  const updateRecord = useCallback(
    (id: string, patch: Partial<UploadRecord>) => {
      safeSetUploads((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    },
    [safeSetUploads],
  );

  const scheduleAutoDismiss = useCallback(
    (id: string) => {
      const existing = autoDismissTimersRef.current.get(id);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => {
        autoDismissTimersRef.current.delete(id);
        safeSetUploads((prev) => prev.filter((r) => r.id !== id));
      }, AUTO_DISMISS_SUCCESS_MS);
      autoDismissTimersRef.current.set(id, t);
    },
    [safeSetUploads],
  );

  const cancel = useCallback(
    (id: string) => {
      const ctrl = abortControllersRef.current.get(id);
      // Failed rows use this same callback as "Dismiss" — removing
      // them immediately avoids a flicker where the row briefly
      // shows the active / cancelled styling before it disappears.
      // Once the Rust attachment send has started we cannot abort the
      // underlying future from JS, so do not fake a cancelled state.
      const latest = uploadsRef.current.find((r) => r.id === id);
      if (latest?.status === "failed") {
        safeSetUploads((prev) => prev.filter((r) => r.id !== id));
        return;
      }
      if (latest?.status === "sending") return;
      if (ctrl) ctrl.abort();
      updateRecord(id, { status: "cancelled" });
      scheduleAutoDismiss(id);
    },
    [updateRecord, scheduleAutoDismiss, safeSetUploads],
  );

  const processOne = useCallback(
    async (record: UploadRecord, roomId: string, client: Client) => {
      const controller = new AbortController();
      abortControllersRef.current.set(record.id, controller);
      try {
        if (controller.signal.aborted) return;
        const target: UploadTarget = {
          roomId,
          thread: threadTarget(record.threadRootId, threadRef.current?.()),
          // Only the native binding shows a sent file at once; elsewhere the
          // row arrives after the upload, too late to travel.
          arrive: (send) =>
            arriveWithTransition(
              platform === "tauri" ? (sendListRef.current?.() ?? null) : null,
              send,
            ),
        };
        if (record.file.type.startsWith("image/")) {
          await uploadImage(client, target, record, controller.signal, updateRecord);
        } else if (record.file.type.startsWith("video/")) {
          await uploadVideo(client, target, record, controller.signal, updateRecord);
        } else {
          await uploadGenericFile(client, target, record, controller.signal, updateRecord);
        }
        updateRecord(record.id, { status: "done", progress: 100 });
        scheduleAutoDismiss(record.id);
      } catch (err) {
        if (isAbortError(err)) {
          // `cancel()` already flipped the record to `cancelled`; do
          // not overwrite. Auto-dismiss is already scheduled.
          return;
        }
        const message = errorMessage(err);
        console.warn("upload failed", err);
        updateRecord(record.id, {
          status: "failed",
          error: message,
          reason: classifyFailure(message),
        });
      } finally {
        abortControllersRef.current.delete(record.id);
      }
    },
    [updateRecord, scheduleAutoDismiss],
  );

  // Single shared queue so a second `uploadFiles` invocation (e.g.
  // paste arriving while a picker upload is in flight) tails onto the
  // existing run instead of racing it. Without this the hook would
  // serialise within a single call but parallelise across calls,
  // breaking the ticket's "handle uploads sequentially" rule.
  const queueTailRef = useRef<Promise<void>>(Promise.resolve());

  const uploadFiles = useCallback(
    async (files: readonly File[], roomId: string, threadRootId = threadRef.current?.().rootId) => {
      if (!client) return;
      // Snapshot of new records; appended at the tail of the list so
      // the composer's progress tray shows them in arrival order.
      // `crypto.randomUUID()` avoids the Date-collision risk of
      // `Date.now() + random-suffix` when multiple files are queued in
      // the same millisecond from a single drop / paste.
      const fresh: UploadRecord[] = [];
      for (const file of files) {
        fresh.push({
          id: crypto.randomUUID(),
          file,
          progress: 0,
          status: "uploading",
          threadRootId,
        });
      }
      if (fresh.length === 0) return;
      safeSetUploads((prev) => [...prev, ...fresh]);
      // Chain onto the existing queue tail so concurrent invocations
      // serialise. A failure in the prior chain is swallowed (its
      // own caller already logged / surfaced it) so we don't inherit
      // its rejection here.
      const run = queueTailRef.current
        .catch(() => {})
        .then(async () => {
          for (const record of fresh) {
            // Skip files the user already cancelled while a prior
            // upload was running. An explicit abort-controller per
            // record is registered inside `processOne`; checking
            // `abortControllersRef` here would be premature (we haven't
            // created the controller yet). The ref-based status read
            // is reliable because `cancel()` updates state
            // synchronously and React flushes the state before
            // awaiting further promises in this microtask chain.
            const latest = getLatestStatus(record.id);
            if (latest === "cancelled") continue;
            // Sequential uploads by design — `await` inside the loop
            // is intentional. Ticket's "Out of scope: Simultaneous
            // multi-file progress — handle uploads sequentially."
            await processOne(record, roomId, client);
          }
        });
      queueTailRef.current = run;
      await run;
    },
    [client, processOne, safeSetUploads],
  );

  // Helper that peeks at the live list for the "did the user cancel
  // while we were uploading another file?" check in `uploadFiles`.
  function getLatestStatus(id: string): UploadStatus | null {
    const match = uploadsRef.current.find((r) => r.id === id);
    return match?.status ?? null;
  }

  const retry = useCallback(
    (id: string, roomId: string) => {
      const record = uploadsRef.current.find((r) => r.id === id);
      if (!record) return;
      safeSetUploads((prev) => prev.filter((r) => r.id !== id));
      void uploadFiles([record.file], roomId, record.threadRootId);
    },
    [safeSetUploads, uploadFiles],
  );

  return { uploads, uploadFiles, cancel, retry };
}

function threadTarget(
  rootId: string | undefined,
  current: MediaThreadTarget | undefined,
): MediaThreadTarget | undefined {
  if (!rootId) return undefined;
  if (current?.rootId === rootId) return current;
  return { rootId, latestReplyId: rootId };
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Upload failed";
}

function classifyFailure(message: string): UploadReason {
  return /M_TOO_LARGE|413/.test(message) ? "too_large" : "failed";
}

/* ── per-kind upload routines ─────────────────────────────────── */

interface UploadTarget {
  roomId: string;
  thread?: MediaThreadTarget;
  arrive: (send: () => Promise<void>) => Promise<void>;
}

async function uploadImage(
  client: Client,
  { roomId, thread, arrive }: UploadTarget,
  record: UploadRecord,
  signal: AbortSignal,
  updateRecord: (id: string, patch: Partial<UploadRecord>) => void,
): Promise<void> {
  const { file } = record;
  // Generate a thumbnail first so we can upload both blobs in series.
  // Errors from thumbnailing fall through to sending the original
  // without a thumb — we never fail the send because the thumbnail
  // couldn't be produced.
  const thumb = await generateImageThumbnail(file).catch((err) => {
    console.warn("thumbnail generation failed", err);
    return null;
  });
  if (signal.aborted) throw new DOMException("aborted", "AbortError");

  updateRecord(record.id, { progress: 15 });
  const dims = await readImageDimensions(file).catch(() => null);
  if (signal.aborted) throw new DOMException("aborted", "AbortError");
  updateRecord(record.id, { status: "sending", progress: 85 });
  const base = {
    data: file,
    w: dims?.w,
    h: dims?.h,
    size: file.size,
    mimetype: file.type || "application/octet-stream",
    filename: file.name,
    thread,
  };
  await arrive(async () => {
    await matrixSendImage(
      client,
      roomId,
      thumb
        ? {
            ...base,
            thumbnailData: thumb.blob,
            thumbnailWidth: thumb.width,
            thumbnailHeight: thumb.height,
          }
        : base,
    );
  });
}

async function uploadVideo(
  client: Client,
  { roomId, thread, arrive }: UploadTarget,
  record: UploadRecord,
  signal: AbortSignal,
  updateRecord: (id: string, patch: Partial<UploadRecord>) => void,
): Promise<void> {
  const { file } = record;
  const thumb = await generateVideoThumbnail(file).catch((err) => {
    console.warn("video thumbnail generation failed", err);
    return null;
  });
  if (signal.aborted) throw new DOMException("aborted", "AbortError");

  updateRecord(record.id, { progress: 15 });
  updateRecord(record.id, { status: "sending", progress: 85 });
  const base = {
    data: file,
    size: file.size,
    mimetype: file.type || "application/octet-stream",
    filename: file.name,
    width: thumb?.videoWidth,
    height: thumb?.videoHeight,
    // Matrix `info.duration` is milliseconds; `HTMLMediaElement.duration` is seconds.
    duration: thumb?.duration === undefined ? undefined : Math.round(thumb.duration * 1000),
    thread,
  };
  await arrive(async () => {
    await matrixSendVideo(
      client,
      roomId,
      thumb
        ? {
            ...base,
            thumbnailData: thumb.blob,
            thumbnailWidth: thumb.thumbWidth,
            thumbnailHeight: thumb.thumbHeight,
          }
        : base,
    );
  });
}

async function uploadGenericFile(
  client: Client,
  { roomId, thread, arrive }: UploadTarget,
  record: UploadRecord,
  signal: AbortSignal,
  updateRecord: (id: string, patch: Partial<UploadRecord>) => void,
): Promise<void> {
  const { file } = record;
  updateRecord(record.id, { progress: 15 });
  if (signal.aborted) throw new DOMException("aborted", "AbortError");
  updateRecord(record.id, { status: "sending", progress: 85 });
  await arrive(async () => {
    await matrixSendFile(client, roomId, {
      data: file,
      size: file.size,
      mimetype: file.type || "application/octet-stream",
      filename: file.name,
      thread,
    });
  });
}

/* ── image helpers ─────────────────────────────────────────────── */

/**
 * Read intrinsic pixel dimensions of an image file. Returns `null` on
 * decode failure — callers must not assume the result is present.
 */
async function readImageDimensions(file: File): Promise<{ w: number; h: number } | null> {
  if (typeof Image === "undefined") return null;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const result = { w: img.naturalWidth, h: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(result);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Render `file` onto a 2D canvas and return a JPEG thumbnail clamped
 * to `THUMBNAIL_MAX_DIMENSION` px on the long edge. Returns `null`
 * when the image is already under the cap (no re-encoding needed) or
 * when the canvas context isn't available (SSR / non-browser).
 */
async function generateImageThumbnail(
  file: File,
): Promise<{ blob: Blob; width: number; height: number } | null> {
  if (typeof document === "undefined") return null;
  const dims = await readImageDimensions(file);
  if (!dims) return null;
  // Skip thumbnailing small images — the original is already below
  // the cap so the thumbnail would just be a lossy copy.
  if (dims.w <= THUMBNAIL_MAX_DIMENSION && dims.h <= THUMBNAIL_MAX_DIMENSION) return null;

  const ratio = Math.min(THUMBNAIL_MAX_DIMENSION / dims.w, THUMBNAIL_MAX_DIMENSION / dims.h);
  const targetW = Math.max(1, Math.round(dims.w * ratio));
  const targetH = Math.max(1, Math.round(dims.h * ratio));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("thumbnail: image decode failed"));
      el.src = url;
    });
    ctx.drawImage(img, 0, 0, targetW, targetH);
  } finally {
    URL.revokeObjectURL(url);
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85);
  });
  if (!blob) return null;
  return { blob, width: targetW, height: targetH };
}

/* ── video helpers ─────────────────────────────────────────────── */

async function generateVideoThumbnail(file: File): Promise<{
  blob: Blob;
  thumbWidth: number;
  thumbHeight: number;
  videoWidth: number;
  videoHeight: number;
  duration?: number;
} | null> {
  if (typeof document === "undefined") return null;
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const { videoWidth, videoHeight, duration } = await new Promise<{
      videoWidth: number;
      videoHeight: number;
      duration: number;
    }>((resolve, reject) => {
      video.onloadedmetadata = () => {
        video.currentTime = 0.1;
      };
      video.onseeked = () => {
        resolve({
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          duration: video.duration,
        });
      };
      video.onerror = () => reject(new Error("thumbnail: video decode failed"));
      video.src = url;
    });
    if (!videoWidth || !videoHeight) return null;

    const ratio = Math.min(
      THUMBNAIL_MAX_DIMENSION / videoWidth,
      THUMBNAIL_MAX_DIMENSION / videoHeight,
      1,
    );
    const thumbWidth = Math.max(1, Math.round(videoWidth * ratio));
    const thumbHeight = Math.max(1, Math.round(videoHeight * ratio));

    const canvas = document.createElement("canvas");
    canvas.width = thumbWidth;
    canvas.height = thumbHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85);
    });
    if (!blob) return null;
    return {
      blob,
      thumbWidth,
      thumbHeight,
      videoWidth,
      videoHeight,
      duration: Number.isFinite(duration) ? duration : undefined,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
